import React from 'react';
import ShapeRenderer from './ShapeRenderer';

// ─── Feedback Overlay ─────────────────────────────────────────────────────────
function FeedbackOverlay({ type }) {
  if (!type) return null;

  if (type === 'correct') {
    return (
      <div className="sq-feedback-overlay sq-feedback-correct">
        <div className="sq-celebration-pop">
          <span className="sq-celebration-emoji">🌟</span>
          <p className="sq-correct-text">כל הכבוד!</p>
        </div>
      </div>
    );
  }

  if (type === 'complete') {
    return (
      <div className="sq-feedback-overlay sq-feedback-complete">
        <div className="sq-celebration-pop">
          <span className="sq-celebration-emoji big">🎉</span>
          <p className="sq-correct-text">השלמת את הכוכב!</p>
          <p className="sq-sub-text">הכוכב הבא נפתח עכשיו ✨</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sq-feedback-overlay sq-feedback-wrong">
      <div className="sq-try-again-card">
        <span style={{ fontSize: '2.5rem' }}>🤔</span>
        <p className="sq-wrong-text">נסה שוב!</p>
        <p className="sq-sub-text">אתה יכול לזה!</p>
      </div>
    </div>
  );
}

// ─── Visual Puzzle Row ────────────────────────────────────────────────────────
function VisualPuzzle({ puzzle }) {
  return (
    <div className="sq-visual-puzzle">
      {puzzle.elements.map((el, i) => (
        <div key={i} className="sq-visual-puzzle-item">
          <ShapeRenderer shape={el.shape} color={el.color} size={52} />
        </div>
      ))}
    </div>
  );
}

// ─── Option content — string or shape object ──────────────────────────────────
function OptionContent({ option }) {
  if (typeof option === 'object' && option !== null) {
    return <ShapeRenderer shape={option.shape} color={option.color} size={36} />;
  }
  return option;
}

// ─── Question Modal ───────────────────────────────────────────────────────────
export default function QuestionModal({ planet, questions, questionIndex, feedback, onAnswer, onClose }) {
  const question = questions[questionIndex];
  const totalQ = questions.length;
  const disableButtons = !!feedback;

  return (
    /* Backdrop */
    <div className="sq-modal-backdrop" role="dialog" aria-modal="true">
      <div
        className="sq-backdrop-dim"
        onClick={disableButtons ? undefined : onClose}
      />

      {/* Card */}
      <div className="sq-modal-card" dir="rtl">

        {/* ── Header ── */}
        <div className="sq-modal-header">
          <button
            className="sq-close-btn"
            onClick={disableButtons ? undefined : onClose}
            aria-label="סגור"
          >
            ✕
          </button>
          <div className="sq-planet-title">
            <span className="sq-planet-emoji-lg">{planet.emoji}</span>
            <div>
              <div className="sq-planet-name-modal">{planet.name}</div>
              <div className="sq-question-counter">
                שאלה {questionIndex + 1} מתוך {totalQ}
              </div>
            </div>
          </div>
        </div>

        {/* ── Progress dots ── */}
        <div className="sq-progress-dots">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`sq-dot ${
                i < questionIndex
                  ? 'sq-dot-done'
                  : i === questionIndex
                  ? 'sq-dot-active'
                  : 'sq-dot-empty'
              }`}
            />
          ))}
        </div>

        {/* ── Question text ── */}
        <div className="sq-question-box">
          <p className="sq-question-text">{question.text}</p>
        </div>

        {/* ── Visual puzzle (optional) ── */}
        {question.visualPuzzle && (
          <VisualPuzzle puzzle={question.visualPuzzle} />
        )}

        {/* ── Answer buttons ── */}
        <div className="sq-options-grid">
          {question.options.map((option, i) => (
            <button
              key={i}
              onClick={() => !disableButtons && onAnswer(i)}
              disabled={disableButtons}
              className={`sq-option-btn${typeof option === 'object' ? ' sq-option-shape' : ''}`}
              style={{
                '--grad-from': planet.gradFrom,
                '--grad-to': planet.gradTo,
              }}
            >
              <OptionContent option={option} />
            </button>
          ))}
        </div>

        {/* ── Feedback overlay (renders on top of everything inside the card) ── */}
        <FeedbackOverlay type={feedback} />
      </div>
    </div>
  );
}
